import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { ScoreBadge } from './ScoreBadge';
import { cn } from '@/lib/utils';
import { formatNumber, type KeywordGrade } from '@keywordgod/shared';

export interface KeywordRow {
  keyword: string;
  totalSearch?: number;
  blogTotal?: number;
  saturation?: number;
  honeyScore?: number;
  opportunity?: number;
  godScore: number;
  grade: KeywordGrade;
}

type SortKey =
  | 'keyword'
  | 'totalSearch'
  | 'blogTotal'
  | 'saturation'
  | 'honeyScore'
  | 'opportunity'
  | 'godScore';

interface Col {
  key: SortKey;
  label: string;
  align?: 'left' | 'right';
  render?: (r: KeywordRow) => React.ReactNode;
}

export function KeywordTable({
  rows,
  columns = DEFAULT_COLUMNS,
  emptyHint = '데이터가 없습니다',
  clickable = true,
  className,
}: {
  rows: KeywordRow[];
  columns?: Col[];
  emptyHint?: string;
  clickable?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'godScore',
    dir: 'desc',
  });

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = (a[sort.key] as number | string | undefined) ?? 0;
      const bv = (b[sort.key] as number | string | undefined) ?? 0;
      if (typeof av === 'string' || typeof bv === 'string') {
        return sort.dir === 'asc'
          ? String(av).localeCompare(String(bv), 'ko')
          : String(bv).localeCompare(String(av), 'ko');
      }
      return sort.dir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return arr;
  }, [rows, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  }

  if (rows.length === 0) {
    return <div className="text-sm text-gray-500">{emptyHint}</div>;
  }

  return (
    <div className={cn('overflow-x-auto rounded-xl border border-gray-800', className)}>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-900/60 text-xs uppercase tracking-wider text-gray-400">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'px-4 py-3 cursor-pointer select-none',
                  c.align === 'right' ? 'text-right' : 'text-left',
                )}
                onClick={() => toggleSort(c.key)}
              >
                <span className="inline-flex items-center gap-1">
                  {c.label}
                  <SortIcon active={sort.key === c.key} dir={sort.dir} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.keyword}
              className={cn(
                'border-t border-gray-800 hover:bg-gray-800/40 transition-colors',
                clickable && 'cursor-pointer',
              )}
              onClick={
                clickable
                  ? () => navigate(`/analyze/${encodeURIComponent(r.keyword)}`)
                  : undefined
              }
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-4 py-3 tabular',
                    c.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  {c.render ? c.render(r) : defaultRender(c.key, r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function defaultRender(key: SortKey, r: KeywordRow): React.ReactNode {
  switch (key) {
    case 'keyword':
      return <span className="font-medium text-white">{r.keyword}</span>;
    case 'totalSearch':
      return r.totalSearch != null ? formatNumber(r.totalSearch) : '-';
    case 'blogTotal':
      return r.blogTotal != null ? formatNumber(r.blogTotal) : '-';
    case 'saturation':
      return r.saturation != null ? `${r.saturation.toFixed(1)}%` : '-';
    case 'honeyScore':
      return r.honeyScore != null ? r.honeyScore.toFixed(1) : '-';
    case 'opportunity':
      return r.opportunity != null ? r.opportunity.toFixed(2) : '-';
    case 'godScore':
      return (
        <span className="inline-flex items-center gap-2 justify-end">
          <span className="text-white">{Math.round(r.godScore)}</span>
          <ScoreBadge grade={r.grade} size="sm" />
        </span>
      );
  }
}

function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 text-gray-600" />;
  return dir === 'asc' ? (
    <ArrowUp className="h-3 w-3 text-gold" />
  ) : (
    <ArrowDown className="h-3 w-3 text-gold" />
  );
}

const DEFAULT_COLUMNS: Col[] = [
  { key: 'keyword', label: '키워드' },
  { key: 'totalSearch', label: '검색량', align: 'right' },
  { key: 'blogTotal', label: '문서수', align: 'right' },
  { key: 'saturation', label: '포화', align: 'right' },
  { key: 'godScore', label: 'GodScore', align: 'right' },
];
